import type { MachineId, ModifierSource, RuleLine, SimEvent, Tag } from '../shared/contract.js';
import type { TokenRuntime } from './token.js';
import type { Vec3 } from './vec.js';

/**
 * THE MODIFIER PIPELINE — the load-bearing architecture (modifier-grammar.md).
 *
 * One interface, four sources. Enchantments (tags on tokens), Charms (run-global),
 * Manipulationen (per-cabinet installs) and ZINKEN (the House's riggings) are all the
 * same `Modifier`. Machines never ask "do I have item X?"; they ask the pipeline a
 * typed question ("what is gravity here?", "this token scored, anyone care?") and the
 * pipeline answers in a fixed, visible priority order.
 *
 * Provenance is not an afterthought: every hook context carries a `note()` sink, and a
 * modifier that actually *changes* an answer records itself. The Hausregeln board is
 * then just a render of that record, in resolution order, for free.
 */

export type StageId = 'base' | 'material' | 'straehne' | 'topf' | 'floor';

// ---------------------------------------------------------------- contexts

export interface Provenance {
  sourceId: string;
  source: ModifierSource;
  text: string;
  discovered: boolean;
}

export interface HookCtx {
  readonly machine: MachineId;
  readonly tick: number;
  /** the token the question is about, if the question is about a token at all */
  readonly token: TokenRuntime | null;
  /** convenience: token?.tags ?? [] — modifiers gate on tags, never on ids */
  readonly tags: readonly Tag[];
  /** attribute a rewrite to its source (Hausregeln board) */
  note(p: Provenance): void;
}

export interface OddsCtx extends HookCtx {
  readonly stream: string;
  readonly event: string;
}

export interface StageCtx extends HookCtx {
  readonly stage: StageId;
}

export interface EvCtx extends HookCtx {
  readonly api: SimApi;
}

// ---------------------------------------------------------------- effects surface

export interface ScoreInfo {
  token: number;
  pocket: string;
  /** raw base value before the payout pipeline */
  value: number;
  at: Vec3;
  /** jackpot-class scores are what [Spalt] splits on */
  jackpot: boolean;
}

/**
 * What an event hook is allowed to do to the world. Deliberately narrow: modifiers
 * push on the sim through this door only, which is what keeps replays reproducible.
 */
export interface SimApi {
  readonly tick: number;
  readonly machine: MachineId;
  emit(e: SimEvent): void;

  token(id: number): TokenRuntime | null;
  /** live tokens in the current cabinet, in ascending id order (deterministic) */
  liveTokens(): readonly TokenRuntime[];
  /** ids of live tokens within `radius` of `id`, ascending (excludes `id`) */
  neighbours(id: number, radius: number): number[];

  position(id: number): Vec3 | null;
  impulse(id: number, v: Vec3): void;
  /**
   * First solid thing this token currently overlaps: another token's id, or -1 for
   * field geometry (pegs, walls, shelf), or null for "nothing". Core owns all cabinet
   * geometry as data, so this is exact arithmetic rather than a physics-engine query —
   * which keeps [Geist] bit-deterministic and independent of Rapier's query API.
   */
  solidOverlap(id: number): number | null;
  /** weld a token into the field — it becomes terrain ([Kleber]) */
  freeze(id: number): void;
  /** [Geist] collision masking */
  setGhost(id: number, on: boolean): void;

  setFaceValue(id: number, v: number): void;
  /** [Spalt]: two half-value children inheriting enchants at half potency */
  splitToken(id: number): [number, number] | null;
  /** [Echo]: replay a scoring event later, at a fraction of its value */
  scheduleEcho(id: number, value: number, delaySec: number): void;

  addTopf(amount: number): void;
  /** award coins/tickets through the *full* payout pipeline */
  score(info: ScoreInfo): void;
  verdacht(delta: number, reason: string): void;
  glueck(delta: number): void;
}

// ---------------------------------------------------------------- hooks

/** Value questions. Every one is `(ctx, value) => value` so they compose by folding. */
export interface QueryHooks {
  gravity(ctx: HookCtx, v: Vec3): Vec3;
  mass(ctx: HookCtx, v: number): number;
  restitution(ctx: HookCtx, v: number): number;
  friction(ctx: HookCtx, v: number): number;
  /** material multiplier at the material stage */
  materialMult(ctx: HookCtx, v: number): number;
  /** pocket / target mouth width scale — [Gluecks] widens */
  pocketWidth(ctx: HookCtx, v: number): number;
  /** probability of the "good" outcome, 0..1 — [Fluch] inverts */
  odds(ctx: OddsCtx, p: number): number;
  /** the payout pipeline itself; rewiring items splice this array */
  stageOrder(ctx: HookCtx, order: StageId[]): StageId[];
  /**
   * The value *leaving* one named stage. economy.md's item design space is the wiring:
   * reorder stages, fork the stream, **tap a stage**, overflow it. `stageOrder` covers
   * reordering; this covers tapping — a skim, a fork, an overflow valve. The House's
   * Abschöpfer taps the floor stage with it, and a Brecheisen turns that skim around.
   */
  stageValue(ctx: StageCtx, v: number): number;
  /** share of payout paid as tickets rather than coins */
  ticketRatio(ctx: HookCtx, v: number): number;
  /** cabinet structural load scale — Bohrloch-class mods make banks fragile */
  limitScale(ctx: HookCtx, v: number): number;
  /** how much one shove adds to the TILT meter; Die Kamera halves your room to shove in */
  tiltScale(ctx: HookCtx, v: number): number;
  /**
   * What happens to a token that leaves this cabinet? Default 'lose' (the House keeps
   * it). Der Deckel answers 'room': the arcade is one physical space and the token
   * rolls into the neighbouring machine. Machines ask this question; they never ask
   * whether the player owns a particular charm.
   */
  escape(ctx: HookCtx, v: EscapeMode): EscapeMode;
}

/**
 * 'lose'  — the House keeps it (default).
 * 'room'  — Der Deckel: the arcade is one physical space; it rolls into the neighbour.
 * 'block' — something of yours is in the way. Der Riesenplüschbär is a soft wall you own:
 *           the token is shoved back into the machine instead of leaving it. A prize that
 *           is a physics object rather than a number, which is the whole point of him.
 */
export type EscapeMode = 'lose' | 'room' | 'block';

/** Things that happened. Hooks may push back through `ctx.api`. */
export interface EventHooks {
  onStake(ctx: EvCtx, p: { tokens: number[] }): void;
  onDrop(ctx: EvCtx, p: { token: number }): void;
  onCollision(
    ctx: EvCtx,
    p: {
      a: number;
      b: number | null;
      impulse: number;
      at: Vec3;
      /** the other side is something a token may weld itself to (field, ground, or an
       *  already-stuck token) — as opposed to cabinet walls and glass */
      anchor: boolean;
    },
  ): void;
  onScore(ctx: EvCtx, p: ScoreInfo): void;
  onDud(ctx: EvCtx, p: { token: number | null }): void;
  onPayoutStage(ctx: EvCtx, p: { stage: StageId; before: number; after: number }): void;
  onRoll(ctx: EvCtx, p: { stream: string; event: string; win: boolean }): void;
  onCashOut(ctx: EvCtx, p: { muenzen: number; tickets: number }): void;
  onBust(ctx: EvCtx, p: Record<string, never>): void;
  onMachineTick(ctx: EvCtx, p: { dt: number }): void;
  /** per-live-token tick; ctx.token is set. This is where tag physics defaults live. */
  onTokenTick(ctx: EvCtx, p: { dt: number }): void;
  onTokenIdle(ctx: EvCtx, p: { token: number; seconds: number }): void;
  onBankBreak(ctx: EvCtx, p: { payout: number }): void;
  onFloorStart(ctx: EvCtx, p: { floor: number }): void;
  onAbrechnung(ctx: EvCtx, p: { due: number }): void;
}

export interface Modifier {
  id: string;
  source: ModifierSource;
  tags: Tag[];
  /** fixed, visible resolution order; ties broken by id so ordering is total */
  priority: number;
  /** player-facing German rule text for the Hausregeln board */
  ruleText?: string;
  /** ZINKEN start undiscovered and render as a crooked "?" stamp */
  discovered?: boolean;
  /** limit this modifier to certain cabinets (Manipulationen, ZINKEN) */
  machines?: MachineId[];
  /**
   * Perf gate, not semantics: tag defaults only care about tokens carrying their tag,
   * so the sim skips per-token ticks for untagged tokens (the 300-token pile!) unless
   * some modifier — e.g. the Zinseszins charm — declares it wants every token.
   */
  everyToken?: boolean;
  queries?: Partial<QueryHooks>;
  events?: Partial<EventHooks>;
}

// ---------------------------------------------------------------- the pipeline

type QueryName = keyof QueryHooks;
type EventName = keyof EventHooks;

export class Pipeline {
  private mods: Modifier[] = [];
  /**
   * Provenance, PER CABINET — feeds CabinetState.hausregeln. Keeping one global list
   * meant every board printed all twelve tag rules whether or not any token in that
   * machine carried the tag, which is precisely the stacked-rule soup this design
   * treats as the genre's failure mode (risk gate R4).
   */
  private rules = new Map<MachineId, Map<string, RuleLine>>();

  add(m: Modifier): void {
    if (this.mods.some((x) => x.id === m.id)) return;
    this.mods.push(m);
    this.sort();
  }

  addAll(ms: readonly Modifier[]): void {
    for (const m of ms) this.mods.push(m);
    this.mods = dedupe(this.mods);
    this.sort();
  }

  remove(id: string): Modifier | null {
    const i = this.mods.findIndex((m) => m.id === id);
    if (i < 0) return null;
    const [m] = this.mods.splice(i, 1);
    return m ?? null;
  }

  has(id: string): boolean {
    return this.mods.some((m) => m.id === id);
  }

  find(id: string): Modifier | null {
    return this.mods.find((m) => m.id === id) ?? null;
  }

  /**
   * Is there a House rigging on this cabinet that the player has not found yet? The
   * aisle renders it as a crooked "?" stamp on the board — visible suspicion, no detail.
   */
  hasUndiscovered(machine: MachineId): boolean {
    return this.mods.some(
      (m) => m.source === 'zinken' && m.discovered === false && this.applies(m, machine),
    );
  }

  /** True if any modifier wants per-token ticks even for untagged tokens. */
  get needsEveryTokenTick(): boolean {
    return this.mods.some((m) => m.everyToken === true);
  }

  list(): readonly Modifier[] {
    return this.mods;
  }

  private sort(): void {
    // total order: priority, then id. Never depends on insertion order.
    this.mods.sort((a, b) => (a.priority - b.priority) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  }

  private applies(m: Modifier, machine: MachineId): boolean {
    return !m.machines || m.machines.indexOf(machine) >= 0;
  }

  /** Fold every subscribed modifier over a value, in resolution order. */
  query<K extends QueryName>(
    name: K,
    ctx: Omit<Parameters<QueryHooks[K]>[0], 'note'> & { note?: HookCtx['note'] },
    value: Parameters<QueryHooks[K]>[1],
  ): ReturnType<QueryHooks[K]> {
    let v = value as never;
    const self = this;
    let current: Modifier | null = null;
    const full = ctx as unknown as HookCtx & { note: HookCtx['note'] };
    const machine = (ctx as HookCtx).machine;
    full.note = (p: Provenance) => self.record(machine, p);
    for (const m of this.mods) {
      const fn = m.queries?.[name] as ((c: unknown, x: unknown) => unknown) | undefined;
      if (!fn) continue;
      if (!this.applies(m, (ctx as HookCtx).machine)) continue;
      current = m;
      const before = v;
      const out = fn(full, v) as never;
      if (!same(before, out)) {
        // a modifier that actually changed the answer owes the board an explanation
        this.record(machine, {
          sourceId: current.id,
          source: current.source,
          text: current.ruleText ?? current.id,
          discovered: current.discovered !== false,
        });
      }
      v = out;
    }
    return v as ReturnType<QueryHooks[K]>;
  }

  /** Notify every subscribed modifier, in resolution order. */
  fire<K extends EventName>(
    name: K,
    ctx: Omit<EvCtx, 'note'>,
    payload: Parameters<EventHooks[K]>[1],
  ): void {
    const self = this;
    const full = ctx as unknown as EvCtx & { note: HookCtx['note'] };
    full.note = (p: Provenance) => self.record(ctx.machine, p);
    for (const m of this.mods) {
      const fn = m.events?.[name] as ((c: unknown, p: unknown) => void) | undefined;
      if (!fn) continue;
      if (!this.applies(m, ctx.machine)) continue;
      fn(full, payload);
    }
  }

  private record(machine: MachineId, p: Provenance): void {
    let board = this.rules.get(machine);
    if (!board) {
      board = new Map<string, RuleLine>();
      this.rules.set(machine, board);
    }
    const key = p.sourceId + '|' + p.text;
    if (board.has(key)) return;
    board.set(key, {
      text: p.text,
      sourceId: p.sourceId,
      source: p.source,
      discovered: p.discovered,
    });
  }

  /**
   * Hausregeln board content: resolution order = array order (contract).
   *
   * A cabinet's board shows (a) every rule that has actually rewritten something on
   * THIS cabinet, and (b) the run-global charms and the House's installs that apply
   * here — those are worth reading before they fire. Tag defaults are deliberately
   * not pre-listed: "[Schwer] triples mass" is noise until you bring a heavy coin.
   */
  boardFor(machine: MachineId): RuleLine[] {
    const order = new Map<string, number>();
    this.mods.forEach((m, i) => order.set(m.id, i));
    const seen = [...(this.rules.get(machine)?.values() ?? [])];
    for (const m of this.mods) {
      if (!m.ruleText || m.source === 'enchant' || !this.applies(m, machine)) continue;
      if (seen.some((r) => r.sourceId === m.id)) continue;
      seen.push({
        text: m.ruleText,
        sourceId: m.id,
        source: m.source,
        discovered: m.discovered !== false,
      });
    }
    seen.sort((a, b) => (order.get(a.sourceId) ?? 1e9) - (order.get(b.sourceId) ?? 1e9));
    return seen;
  }

  clearBoard(machine?: MachineId): void {
    if (machine) this.rules.delete(machine);
    else this.rules.clear();
  }
}

function dedupe(ms: Modifier[]): Modifier[] {
  const seen = new Set<string>();
  const out: Modifier[] = [];
  for (const m of ms) {
    if (seen.has(m.id)) continue;
    seen.add(m.id);
    out.push(m);
  }
  return out;
}

function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => x === b[i]);
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const av = a as Record<string, unknown>;
    const bv = b as Record<string, unknown>;
    const ak = Object.keys(av);
    return ak.length === Object.keys(bv).length && ak.every((k) => av[k] === bv[k]);
  }
  return false;
}

/** Build a query/event context without repeating the boilerplate at 40 call sites. */
export function ctxFor(
  machine: MachineId,
  tick: number,
  token: TokenRuntime | null,
): Omit<HookCtx, 'note'> {
  return { machine, tick, token, tags: token ? token.tags : EMPTY_TAGS };
}

const EMPTY_TAGS: readonly Tag[] = Object.freeze([]);
